import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { HTTPError, post } from "common/helpers/HTTP";

import useUpdateConversation from "./useUpdateConversation";

import type {
  AudioUploadResponse,
  ConversationMessage,
  FileUploadResponse,
} from "../types";

export interface FileUploadProps {
  accountId: string;
}

async function uploadFileRequest(
  workspaceId: string,
  accountId: string,
  file: Blob,
  filename: string,
): Promise<FileUploadResponse> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("file_name", filename);

  const response = await post(
    `workspaces/${workspaceId}/accounts/${accountId}/conversations/upload-file`,
    formData,
  );

  return response;
}

// Multipart form data upload for audio files
async function uploadAudioRequest(
  workspaceId: string,
  accountId: string,
  file: Blob,
  filename: string,
  recipients: string[],
): Promise<AudioUploadResponse> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("file_name", filename);

  // Add each recipient as a separate form field
  recipients.forEach((recipient) => {
    formData.append("recipients", recipient);
  });

  const response = await post(
    `workspaces/${workspaceId}/accounts/${accountId}/conversations/send-audio`,
    formData,
  );

  return response;
}

async function getAudioDuration(blob: Blob): Promise<number> {
  return new Promise((resolve) => {
    function fallbackToHTML5Audio() {
      const audio = new Audio();
      const url = URL.createObjectURL(blob);
      let isResolved = false;

      const resolveOnce = (duration: number) => {
        if (!isResolved) {
          isResolved = true;
          URL.revokeObjectURL(url);
          resolve(duration);
        }
      };

      // add timeout to prevent memory leak
      const timeout = setTimeout(() => {
        resolveOnce(0);
      }, 15000);

      const checkDuration = () => {
        if (
          audio.duration &&
          Number.isFinite(audio.duration) &&
          audio.duration > 0
        ) {
          clearTimeout(timeout);
          resolveOnce(audio.duration * 1000);
          return true;
        }
        return false;
      };

      audio.addEventListener(
        "loadedmetadata",
        () => {
          if (!checkDuration()) {
            // If duration is still not available, try to load more data
            audio.load();
          }
        },
        { once: true },
      );

      audio.addEventListener(
        "canplaythrough",
        () => {
          if (!checkDuration()) {
            // Try to get duration after audio is fully loaded
            setTimeout(() => {
              if (!checkDuration()) {
                resolveOnce(0);
              }
            }, 100);
          }
        },
        { once: true },
      );

      audio.addEventListener(
        "durationchange",
        () => {
          checkDuration();
        },
        { once: true },
      );

      audio.addEventListener(
        "error",
        () => {
          clearTimeout(timeout);
          resolveOnce(0);
        },
        { once: true },
      );

      // Set audio properties to help with loading
      audio.preload = "metadata";
      audio.src = url;
    }

    // Try Web Audio API first
    const AudioContextClass =
      window.AudioContext ||
      (window as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (AudioContextClass) {
      const audioContext = new AudioContextClass();
      const reader = new FileReader();

      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target?.result as ArrayBuffer;
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          const duration = audioBuffer.duration * 1000; // Convert to milliseconds
          audioContext.close();
          resolve(duration);
        } catch (_error) {
          audioContext.close();
          // Fall back to HTML5 Audio method
          fallbackToHTML5Audio();
        }
      };

      reader.onerror = () => {
        fallbackToHTML5Audio();
      };

      reader.readAsArrayBuffer(blob);
    } else {
      // Fall back to HTML5 Audio
      fallbackToHTML5Audio();
    }
  });
}

export default function useFileUpload({ accountId }: FileUploadProps) {
  const { id: workspaceId, accounts } = useSelectedWorkspaceContext();
  const { updateConversation, addMessage } = useUpdateConversation();

  const { mutateAsync: uploadFile, isPending: isUploading } = useMutation({
    mutationFn: ({ file, filename }: { file: Blob; filename: string }) =>
      uploadFileRequest(workspaceId, accountId, file, filename),
    onError: (error) => {
      if (error instanceof HTTPError && error.code === 415) {
        toast.error(
          "Failed to upload file, unsupported file type. Please try again.",
        );
      }
    },
  });

  const { mutateAsync: uploadAudio, isPending: isAudioUploading } = useMutation(
    {
      mutationFn: async ({
        file,
        filename,
        recipients,
      }: {
        file: Blob;
        filename: string;
        recipients: string[];
      }) => {
        const response = await uploadAudioRequest(
          workspaceId,
          accountId,
          file,
          filename,
          recipients,
        );

        // Get audio duration from the blob
        const duration = await getAudioDuration(file);

        return {
          response,
          duration,
          file,
          filename,
          recipients,
        };
      },
      onSuccess: async ({ response, duration, file, filename }) => {
        try {
          const senderAccount = accounts?.find((acc) => acc.id === accountId);
          const audioUrl = URL.createObjectURL(file);

          const newMessage: ConversationMessage = {
            urn: response.conversation_urn,
            body: "", // audio doesn't have text body, it's empty string
            created_at: response.created_at,
            sender: {
              id: accountId,
              full_name: senderAccount?.full_name,
              picture_url: senderAccount?.picture_url,
            },
            inmail: false,
            audio: {
              id: filename,
              url: audioUrl,
              duration,
            },
            deleted: false,
            edited: false,
          };

          const conversationUrn = response.conversation_urn;

          updateConversation(
            conversationUrn,
            (draftConversation) => {
              draftConversation.last_message = newMessage;
            },
            accountId,
          );
          addMessage(conversationUrn, newMessage);
        } catch (_error) {
          toast.error("Failed to process audio message. Please try again.");
        }
      },
      onError: () => {
        toast.error("Failed to upload audio file. Please try again.");
      },
    },
  );

  return {
    uploadFile,
    isUploading,
    uploadAudio,
    isAudioUploading,
  };
}
